import i18n from 'i18n-js';
import moment from 'moment';
import { scale } from '@components/frame';
import { DATA_GRID_IMAGE_COLUMN_WIDTH } from '@v2/pages/common/constants/base.constants';

import { KEYS_TAGS } from '@components/imageHandling/common';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { navigateBack } from '@helpers';
import { trackSearchResultTapped } from '@services/tracking';

interface ProductionOrderItem {
  productionOrderId: number;
  productionOrderNumber?: string;
  imageHash?: string;
  imageDocumentId?: number;
  [key: string]: any;
}

export const buildNavigationParams = (item: ProductionOrderItem, levelSubScreen = LevelSubScreen.GENERAL) => ({
  mainScreen: ProductionOrderDetailsScreenName,
  subScreen: ProductionOrderDetailsScreen.ORDER,
  levelSubScreen,
  params: {
    perspective: Perspective.ORDER,
    productionOrderId: item.productionOrderId,
    selectedImageKey: item.imageHash,
    documentId: item.imageDocumentId,
  },
});

export const buildAttachmentData = (item: ProductionOrderItem, attachImageId: number) => ({
  documentId: attachImageId,
  documentLinks: [
    {
      scopeId: KEYS_TAGS.ProductionOrder,
      scopeReferenceId: item.productionOrderId,
      scopeReferenceName: item.productionOrderNumber,
      isFavorite: false,
      isActivated: true,
      isFavoriteChanged: false,
    },
  ],
  documentTags: [],
});

interface HandleOpenParams {
  item: ProductionOrderItem;
  levelSubScreen?: string;
  isPopupMode?: boolean;
  onItemSelect?: (item: ProductionOrderItem, type: string) => void;
  attachMode?: boolean;
  attachImageId?: number;
  updateImageMetadata: (data: any) => void;
  navigateToMainDetailsScreen: (params: any) => void;
}

export const handleOpen = ({
  item,
  levelSubScreen = LevelSubScreen.GENERAL,
  isPopupMode,
  onItemSelect,
  attachMode,
  attachImageId,
  updateImageMetadata,
  navigateToMainDetailsScreen,
}: HandleOpenParams) => {
  trackSearchResultTapped('ProductionOrder');

  if (isPopupMode && onItemSelect) {
    onItemSelect(item, 'ProductionOrder');
    return;
  }

  if (attachMode && attachImageId) {
    const backendData = buildAttachmentData(item, attachImageId);
    updateImageMetadata(backendData);
    navigateBack();
    return;
  }

  navigateToMainDetailsScreen(buildNavigationParams(item, levelSubScreen));
};

const makeDateColumn = (field: string, headerKey: string, widthValue = 150) => ({
  field,
  headerName: i18n.t(headerKey),
  align: 'left' as const,
  width: scale(widthValue),
  type: 'date' as const,
  sortable: true,
  filterable: true,
  valueGetter: (value: string | null) => (value ? new Date(value) : value),
  valueFormatter: (value: Date | null) => (value ? moment(value).format('DD/MM/YYYY') : ''),
});

export const getDataGridColumns = (openFn: (item: any, levelSubScreen?: string) => void, imageRenderFn: (item: any) => any) => [
  {
    field: 'Image',
    headerName: '',
    align: 'center' as const,
    width: scale(DATA_GRID_IMAGE_COLUMN_WIDTH),
    sortable: false,
    filterable: false,
    renderCell: ({ row: item }: { row: any }) => imageRenderFn(item),
  },
  { field: 'productionOrderNumber', headerName: i18n.t('Production Order'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'description', headerName: i18n.t('Description'), align: 'left', width: scale(200), sortable: true, filterable: false },
  { field: 'quantityPlanned', headerName: i18n.t('Quantity'), align: 'left', width: scale(150), sortable: true, filterable: true },
  {
    field: 'isActive',
    headerName: i18n.t('Not Completed'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
    type: 'boolean',
    valueFormatter: (value: boolean) => i18n.t(value ? 'Yes' : 'No'),
  },
  makeDateColumn('scheduledStartDate', 'Date Scheduled Start'),
  makeDateColumn('actualStartDate', 'Date Actual Start'),
  makeDateColumn('scheduledEndDate', 'Date Scheduled End'),
  makeDateColumn('actualEndDate', 'Date Actual End'),
  makeDateColumn('internalDeliveryDate', 'Date Internal Delivery'),
  makeDateColumn('customerDeliveryDate', 'Date Customer Agreed Delivery', 175),
  makeDateColumn('modifiedOn', 'Date Modified'),
  makeDateColumn('createdOn', 'Date Created'),
  { field: 'productionOrderRowMemo', headerName: i18n.t('Memo'), align: 'left', width: scale(150), sortable: true, filterable: false },
  { field: 'recipeNumber', headerName: i18n.t('Routing Number'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partNumber', headerName: i18n.t('Part Name'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDescription', headerName: i18n.t('Part Description'), align: 'left', width: scale(150), sortable: true, filterable: false },
  { field: 'partDrawingNumber', headerName: i18n.t('Drawing Number'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingRevision', headerName: i18n.t('Drawing Revision'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingPosition', headerName: i18n.t('Drawing Position'), align: 'left', width: scale(150), sortable: true, filterable: true },
];
